import React, { useState, useEffect, useRef } from "react";
import { ArrowUp, Mail, Star, TrendingUp, X, ChevronUp, Check } from "lucide-react";
import { Link } from "react-router-dom";

const useIntersectionObserver = (options = {}) => {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const targetRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      setIsIntersecting(entry.isIntersecting);
    }, options);

    if (targetRef.current) {
      observer.observe(targetRef.current);
    }

    return () => {
      if (targetRef.current) {
        observer.unobserve(targetRef.current);
      }
    };
  }, []);

  return [targetRef, isIntersecting];
};

const useCountUp = (end, duration = 2000, start = 0, isStarted = false) => {
  const [count, setCount] = useState(start);
  
  useEffect(() => {
    if (!isStarted) return;
    
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      
      setCount(Math.floor(progress * (end - start) + start));
      
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    
    window.requestAnimationFrame(step);
  }, [end, duration, start, isStarted]);
  
  return count;
};

const AnimatedStat = ({ end, duration, decimals = 0, suffix = '' }) => {
  const [ref, isVisible] = useIntersectionObserver({ threshold: 0.1 });
  const value = useCountUp(end * (decimals ? Math.pow(10, decimals) : 1), duration, 0, isVisible);
  
  return (
    <div ref={ref} className="text-3xl font-bold mb-1">
      {decimals ? (value / Math.pow(10, decimals)).toFixed(decimals) : value}{suffix}
    </div>
  );
};

const BoostImpressions = () => {
  const [selectedTab, setSelectedTab] = useState("overview");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white">
  

      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-purple-600/10 to-indigo-600/5 z-0"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24 relative z-10">
          <div className="max-w-3xl">
            <span className="mb-4 inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800 border border-purple-200">
              Pro & Enterprise Feature
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
              Boost Your Impressions
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              Get featured in our high-engagement newsletters and reach thousands of decision-makers in your target market.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to='/subscription' className="inline-flex items-center justify-center px-6 py-3 rounded-md text-white bg-purple-600 hover:bg-purple-700 font-medium transition-colors">
                <Star className="mr-2 h-5 w-5" />
                Upgrade to Pro
              </Link>

            </div>
          </div>
        </div>
        
        {/* Statistics overlay */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 -mt-8">
            <div className="bg-white/90 backdrop-blur-sm shadow-lg rounded-lg p-6 border-0">
              <div className="text-center">
                <AnimatedStat end={13} suffix="K+" />
                <p className="text-gray-500">Weekly Newsletter Subscribers</p>
              </div>
            </div>
            <div className="bg-white/90 backdrop-blur-sm shadow-lg rounded-lg p-6 border-0">
              <div className="text-center">
                <AnimatedStat end={32} suffix="%" />
                <p className="text-gray-500">Avg. Open Rate</p>
              </div>
            </div>
            <div className="bg-white/90 backdrop-blur-sm shadow-lg rounded-lg p-6 border-0">
              <div className="text-center">
                <AnimatedStat end={3.4} decimals={1} suffix="x" />
                <p className="text-gray-500">Avg. Conversion Boost</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="w-full">

            
            <div className={`pt-6 ${selectedTab === "overview" ? "block" : "hidden"}`}>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <h2 className="text-2xl font-semibold mb-6">Newsletter Features</h2>
                  <div className="space-y-8">
                    <div className="flex gap-4">
                      <div className="mt-1 bg-purple-100 p-2 rounded-full">
                        <Mail className="h-6 w-6 text-purple-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-lg">Weekly Newsletter</h3>
                        <p className="text-gray-600 mt-1">
                          Get featured in our weekly newsletter sent to over 13,000 buyers and decision-makers.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="mt-1 bg-blue-100 p-2 rounded-full">
                        <TrendingUp className="h-6 w-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-lg">Targeted Audience</h3>
                        <p className="text-gray-600 mt-1">
                          Reach decision-makers in your specific industry vertical with targeted placement options.
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-4">
                      <div className="mt-1 bg-teal-100 p-2 rounded-full">
                        <ArrowUp className="h-6 w-6 text-teal-600" />
                      </div>
                      <div>
                        <h3 className="font-medium text-lg">Enhanced Visibility</h3>
                        <p className="text-gray-600 mt-1">
                          Premium placement ensures your product stands out with eye-catching display.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="lg:pl-8 lg:border-l border-gray-200">
                  <h2 className="text-2xl font-semibold mb-6">Newsletter Types</h2>
                  <div className="space-y-6">
                    <div className="border rounded-lg overflow-hidden shadow-sm">
                      <div className="p-4 pb-2">
                        <h3 className="text-lg font-semibold">Weekly Product Spotlight</h3>
                        <p className="text-sm text-gray-500">Every Wednesday</p>
                      </div>
                      <div className="px-4 py-2">
                        <p className="text-sm text-gray-600">
                          Featured placement in our weekly newsletter highlighting the most innovative products.
                        </p>
                      </div>
                      <div className="p-4 pt-0">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200">
                          Pro Plan
                        </span>
                      </div>
                    </div>
                    
                    <div className="border rounded-lg overflow-hidden shadow-sm">
                      <div className="p-4 pb-2">
                        <h3 className="text-lg font-semibold">Bi-Monthly In-Depth Feature</h3>
                        <p className="text-sm text-gray-500">1st and 15th of every month</p>
                      </div>
                      <div className="px-4 py-2">
                        <p className="text-sm text-gray-600">
                          Comprehensive product showcases with testimonials and use cases in our Substack publication.
                        </p>
                      </div>
                      <div className="p-4 pt-0">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                          Enterprise Plan
                        </span>
                      </div>
                    </div>
                  
                  </div>
                </div>
              </div>
            </div>
            
            <div className={`pt-6 ${selectedTab === "analytics" ? "block" : "hidden"}`}>
              <div className="border rounded-lg shadow-sm">
                <div className="p-4 border-b">
                  <h2 className="text-lg font-semibold">Your Boost Analytics</h2>
                  <p className="text-sm text-gray-500">
                    Track the performance of your newsletter features and audience reach
                  </p>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Upgrade to Get Featured</h2>
            <p className="mt-4 text-xl text-gray-600 max-w-2xl mx-auto">
              Choose the plan that's right for you and start boosting your impressions today.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="border-2 border-purple-100 hover:border-purple-200 transition-all duration-200 rounded-lg shadow-sm">
              <div className="p-6 border-b">
                <div className="flex items-center gap-2">
                  <span className="bg-purple-100 p-2 rounded-full">
                    <Star className="h-5 w-5 text-purple-600" />
                  </span>
                  <h3 className="text-lg font-semibold">Pro Plan</h3>
                </div>
                <p className="text-sm text-gray-500">For growing companies</p>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <span className="text-3xl font-bold">$99</span>
                  <span className="text-gray-500">/month</span>
                </div>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Weekly newsletter features
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Basic analytics dashboard
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Up to 1 feature per month
                  </li>
                </ul>
              </div>
              <div className="p-6 pt-0">
                <Link to='/subscription' className="w-full py-2 px-4 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-md transition-colors">
                  Upgrade to Pro
                </Link>
              </div>
            </div>
            
            <div className="border-2 border-blue-100 hover:border-blue-200 transition-all duration-200 rounded-lg shadow-sm">
              <div className="p-6 border-b">
                <div className="flex items-center gap-2">
                  <span className="bg-blue-100 p-2 rounded-full">
                    <TrendingUp className="h-5 w-5 text-blue-600" />
                  </span>
                  <h3 className="text-lg font-semibold">Enterprise Plan</h3>
                </div>
                <p className="text-sm text-gray-500">For established businesses</p>
              </div>
              <div className="p-6">
                <div className="mb-4">
                  <span className="text-3xl font-bold">$199</span>
                  <span className="text-gray-500">/month</span>
                </div>
                <ul className="space-y-2">
                  <li className="flex items-center gap-2">
                    <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Weekly + Bi-monthly features
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Advanced analytics dashboard
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Up to 3 features per month
                  </li>
                  <li className="flex items-center gap-2">
                    <svg className="h-5 w-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    Industry vertical targeting
                  </li>
                </ul>
              </div>
              <div className="p-6 pt-0">
                <Link to='/subscription' className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md transition-colors">
                  Upgrade to Enterprise
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Frequently Asked Questions</h2>
            <p className="mt-4 text-gray-600 max-w-2xl mx-auto">
              Everything you need to know about boosting your impressions with our newsletter features.
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto divide-y divide-gray-200">
            {[
              {
                q: "How often can my product be featured?",
                a: "It depends on your plan. Pro subscribers can have up to 1 feature per month, while Enterprise subscribers can have up to 3 features per month across all newsletter types."
              },
              {
                q: "How are the analytics measured?",
                a: "We track opens, clicks, and engagement with your feature. Enterprise subscribers also get demographic data on who interacted with your content."
              },
              {
                q: "Can I choose which newsletter my product appears in?",
                a: "Enterprise subscribers can select specific industry verticals and newsletter types. Pro subscribers are featured in our regular weekly newsletter rotation."
              },
              {
                q: "What information is needed for my product feature?",
                a: "We'll need your product name, description, key selling points, a high-quality image, and your landing page URL. Enterprise features may require additional assets."
              },
              {
                q: "How far in advance should I book my feature?",
                a: "We recommend booking at least 2 weeks in advance to secure your spot, especially for high-demand periods."
              }
            ].map((item, i) => (
              <div key={i} className="py-6">
                <h3 className="text-lg font-medium text-gray-900">{item.q}</h3>
                <p className="mt-2 text-gray-600">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-br from-purple-900 to-indigo-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-6">Ready to Boost Your Impressions?</h2>
          <p className="text-lg text-purple-100 max-w-2xl mx-auto mb-8">
            Get your product in front of thousands of potential customers with our newsletter features.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to='/subscription'
              className="px-6 py-3 bg-white text-purple-900 hover:bg-gray-100 font-medium rounded-md transition-colors"
            >
              Get Started
            </Link>

          </div>
        </div>
      </section>

      {/* Dialog */}
      {isDialogOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden">
            <div className="flex justify-between items-start p-6 border-b">
              <div>
                <h3 className="text-lg font-semibold">Boost Your Impressions</h3>
                <p className="text-sm text-gray-500">
                  To get started with newsletter features, you'll need to upgrade your plan.
                </p>
              </div>
              <button 
                onClick={() => setIsDialogOpen(false)} 
                className="text-gray-400 hover:text-gray-500"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-6">
              <p className="text-sm text-gray-500">
                Choose a plan that works for your business needs and start getting featured in our newsletters.
              </p>
            </div>
            <div className="p-4 bg-gray-50 border-t flex justify-end">
              <button 
                onClick={() => setIsDialogOpen(false)} 
                className="w-full py-2 px-4 bg-purple-600 hover:bg-purple-700 text-white font-medium rounded-md transition-colors"
              >
                View Pricing Plans
              </button>
            </div>
          </div>
        </div>
      )}

     
    </div>
  );
};

export default BoostImpressions;